import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import Webcam from 'react-webcam'
import { ContainedButton, OutlinedButton } from '../../../../components/Button'
import { useSnackbar } from '../../../../hooks/snackbar/useSnackbar'
import { useUpload } from '../../../../hooks/upload'
import { TestParameter } from '../../../../types/TestParameter'
import { dataURLtoFile } from '../../../../utils/uploadHelper'
import { TestStripOverlay } from '../../Upload/components/TestStripOverlay'
import { OptionCard, UploadDropzone, UPLOAD_OPTIONS } from '../../Upload/subComponents'
import { TestParameterPicker } from './TestParameterPicker'
import { BrowserOptions } from './uploadManual/BrowserOptions'
import { CameraOptions } from './uploadManual/CameraOptions'
import { UploadOptions } from './uploadManual/UploadOptions'
import { useDispatch, useSelector } from 'react-redux'
import { WaterTestParamsSelectors } from '../../../../store/WaterTestParams'
import { AppDispatch } from '../../../../store'
import { FormsActions } from '../../../../store/forms'
import { Completed } from './uploadManual/Completed'

interface TestParametersContainerProps {

  selectedValues: Record<string, number>
  onValueSelect: (parameterName: string, value: number) => void
  formImageId: string
  flagParameters?: number[] | null
}

export const TestParametersContainer = ({
  selectedValues,
  onValueSelect,
  flagParameters,
  formImageId,
}: TestParametersContainerProps) => {
  const { showError } = useSnackbar()

  const { capturedImage } = useUpload()
  const dispatch = useDispatch<AppDispatch>()

  const [currentState, setCurrentState] = useState<'options' | 'upload' | 'camera' | 'completed'>(
    'options',
  )
  const [showInstructions, setShowInstructions] = useState(false)

  const parameters = useSelector(WaterTestParamsSelectors.makeWaterTestParamsValues)

  const resetUpload = () => {
    setCurrentState('options')
    dispatch(FormsActions.resetFormWithId(formImageId))

  }



  const MAX_FILE_SIZE = 10 * 1024 * 1024
  const handleImageDrop = useCallback(
    (files: File[]) => {
      if (files.length === 0) return

      const file = files[0]
      if (file.size > MAX_FILE_SIZE) {
        showError('Image size must be less than 10MB. Please choose a smaller image.')
        return
      }
      const localUrl: string = URL.createObjectURL(file)
      dispatch(FormsActions.addImage({id:formImageId,localUrl, name: file.name}))
      setCurrentState('completed')
    },
    [showError],
  )




  const renderContent = () => {
    switch (currentState) {
      case 'options':
        return (
          <BrowserOptions
            setCurrentState={setCurrentState}
            setShowInstructions={setShowInstructions} />
        )
      case 'camera':
        return (<CameraOptions
          setCurrentState={setCurrentState}
          handleImageDrop={handleImageDrop} />)

      case 'upload':
        return (
          <UploadOptions
            capturedImage={capturedImage}
            handleImageDrop={handleImageDrop}
            resetUpload={resetUpload} />
        )
      case 'completed':
        return (
          <Completed resetUpload={resetUpload} imageFormId={formImageId} />
        )
    }
  }
  return (
    <Stack
      spacing={2}
      sx={{
        maxWidth: 800,
        mx: 'auto',
        pb: { xs: 12, md: 12, lg: 2 },
        width: '100%',
      }}
    >
      {parameters.map((parameter, index) => (
        <TestParameterPicker
          key={parameter.name}
          parameter={parameter}
          selectedValue={selectedValues[parameter.name]}
          onValueSelect={(value) => onValueSelect(parameter.name, value)}
          index={index}
          flagParameter={flagParameters && flagParameters.includes((parameter as any).sort_value)}
        />
      ))}
      {renderContent()}
    </Stack>
  )
}
